import React from 'react'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { Rect, TreeNodeDatumWithVerdict } from '../../../types/tree'
import { capitalize } from '../../../helpers'
import { EmulationItem } from '../emulation-item'
import { useExport } from '../../../contexts'
import { useTheme } from 'app/modules/shared/context/theme-context'
import { getThemeColor } from 'app/modules/shared/helpers'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { Link } from 'react-router-dom'
import { SignalVerdict } from 'app/modules/reports/types'

export const NODE_HEIGHT = 120

interface ForeignNodeProps {
  node: TreeNodeDatumWithVerdict
  path: string
  rect: Rect
}
export const ForeignNode: React.FC<ForeignNodeProps> = ({ node, path, rect }) => {
  const { _t } = useTypedTranslation()
  const { mode } = useTheme()
  const [show, setShow] = React.useState(false)
  const hoverRef = React.useRef(false)
  const { exporting } = useExport()
  const isHideNode = !!node.attributes?.hiddenCount
  const verdict = node.verdictInfo?.verdict as SignalVerdict

  const style = {
    border: `1px solid ${getThemeColor(mode, `color-${verdict || 'border'}`)}`,
    backgroundColor: node.children
      ? getThemeColor(mode, 'color-background')
      : getThemeColor(mode, 'color-emulation-node'),
    color: getThemeColor(mode, 'color-text'),
    textDecoration: 'none',
    width: rect.width,
    height: rect.height,
    padding: '4px 4px 0',
  }

  const content = isHideNode ? (
    <>
      <h6 className="title ellipsis">{capitalize(node.name)}</h6>
      <section className="contents">
        <i className="item ellipsis text-default">({node.attributes?.hiddenCount} hidden)</i>
      </section>
    </>
  ) : (
    <>
      <h6 className="title ellipsis">
        {capitalize(node.name)}
        {node.attributes?.icon && <img src={`${node.attributes?.icon}`} alt="process" width={20} />}
        {node.name === 'network' && <i className="fas fa-exchange-alt" style={{ width: 24 }} />}
      </h6>

      <section className="contents">
        <p className="item ellipsis text-default">
          {node.name === 'process' && node.attributes?.Program}
          {(node.name === 'file' && node.attributes?.File) || node.attributes?.CommandLine}
          {node.name === 'network' && node.attributes?.URI}
          {node.name === 'script' && node.attributes?.Alias}
          {node.name === 'execute' && node.attributes?.action}
        </p>
      </section>
    </>
  )

  const popoverContent = isHideNode ? (
    <>
      {_t('hidden-notes-note-1')}
      <br />
      {_t('hidden-notes-note-2')}
    </>
  ) : (
    <EmulationItem item={node.attributes?.original as any} />
  )

  const link = '?item=' + node.attributes?.nodeId

  return (
    <foreignObject {...rect}>
      {exporting ? (
        <div style={style} className="node">
          {content}
        </div>
      ) : (
        <OverlayTrigger
          placement="auto"
          trigger={['hover', 'click']}
          aria-hidden={true}
          show={show}
          onToggle={(show: boolean) => {
            if (!show) {
              !hoverRef.current && setShow(false)
            } else {
              setShow(true)
            }
          }}
          delay={{ show: 270, hide: 300 }}
          overlay={
            <Popover
              style={{ zIndex: 1040 }}
              id={`${node.name}-info`}
              title={node.name}
              className="responsive-popover"
              show={show}
              onMouseEnter={() => {
                hoverRef.current = true
              }}
              onMouseLeave={() => {
                hoverRef.current = false
                setShow(false)
              }}
            >
              <Popover.Header className="h3 text-center">{capitalize(node.name)}</Popover.Header>
              <Popover.Body style={{ maxHeight: '700px', overflowY: 'auto' }}>{popoverContent}</Popover.Body>
            </Popover>
          }
        >
          {({ ref, ...triggerHandler }) => (
            <Link ref={ref} {...triggerHandler} style={style} className="node" to={`${path}${link}`}>
              {content}
            </Link>
          )}
        </OverlayTrigger>
      )}
    </foreignObject>
  )
}
